import api from './api';
import type { ApiResponse } from './api';

export type LineKind = 'revenue_adjustment' | 'expense' | 'waiver';
export type MonthStatus = 'estimate' | 'closed' | 'paid';

export interface ProfitShareTerms { id: string; workspace_id: string; effective_month: string; share_pct: number; note: string | null }
export interface ProfitShareLine { id: string; workspace_id: string; month: string; kind: LineKind; amount: number; note: string }

export interface MonthFigures {
  revenue: number;
  cost: number;
  adjustments: number;
  expenses: number;
  net: number;
  carry_in: number;
  waiver: number;
  balance: number;
  share_pct: number | null;
  payout: number;
  carry_out: number;
  company_profit: number;
}

export interface ProfitShareStatement {
  id: string;
  paid_amount: number | null;
  paid_on: string | null;
  paid_note: string | null;
  closed_at: string;
}

export interface MonthRow extends MonthFigures {
  month: string;
  status: MonthStatus;
  statement: ProfitShareStatement | null;
}

export interface WorkspaceProfitShare {
  workspace_id: string;
  workspace_name: string;
  terms: ProfitShareTerms[];
  lines: ProfitShareLine[];
  /** Newest first, from the first share month through this one. */
  months: MonthRow[];
}

export interface OverviewRow extends MonthRow { workspace_id: string; workspace_name: string }
export interface Overview {
  month: string;
  rows: OverviewRow[];
  totals: Pick<MonthFigures, 'revenue' | 'cost' | 'adjustments' | 'expenses' | 'net' | 'payout' | 'carry_out' | 'company_profit'>;
}

const base = '/profit-share';
const ws = (id: string) => `${base}/workspaces/${id}`;

export const profitShareService = {
  overview: async (month: string): Promise<Overview> =>
    (await api.get<ApiResponse<Overview>>(`${base}/overview`, { params: { month } })).data.data!,
  workspace: async (id: string): Promise<WorkspaceProfitShare> =>
    (await api.get<ApiResponse<WorkspaceProfitShare>>(ws(id))).data.data!,
  mine: async (): Promise<WorkspaceProfitShare[]> =>
    (await api.get<ApiResponse<WorkspaceProfitShare[]>>(`${base}/mine`)).data.data!,

  setTerms: async (body: { workspace_id: string; effective_month: string; share_pct: number; note?: string }) => {
    await api.put(`${base}/terms`, body);
  },
  deleteTerms: async (id: string) => { await api.delete(`${base}/terms/${id}`); },

  addLine: async (body: { workspace_id: string; month: string; kind: LineKind; amount: number; note: string }) => {
    await api.post(`${base}/lines`, body);
  },
  editLine: async (id: string, body: Partial<{ month: string; kind: LineKind; amount: number; note: string }>) => {
    await api.patch(`${base}/lines/${id}`, body);
  },
  deleteLine: async (id: string) => { await api.delete(`${base}/lines/${id}`); },

  close: async (id: string, month: string) => { await api.post(`${ws(id)}/months/${month}/close`, {}); },
  reopen: async (id: string, month: string) => { await api.post(`${ws(id)}/months/${month}/reopen`, {}); },
  markPaid: async (id: string, month: string, body: { amount?: number; paid_on?: string; note?: string }) => {
    await api.post(`${ws(id)}/months/${month}/paid`, body);
  },
  unmarkPaid: async (id: string, month: string) => { await api.delete(`${ws(id)}/months/${month}/paid`); },
};
